import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface BookItem {
  bookId: string;
  bookTitle: string;
  price: number;
}

interface LessonItem {
  lessonId: string;
  lessonTitle: string;
  price: number;
}

export interface ShippingData {
  name: string;
  address: string;
  city: string;
  postalCode: string;
  countryCode: string;
  phone: string;
  email: string;
}

export interface TerminalData {
  id: string;
  identifier: string;
  name: string;
  courier_code: string;
  address: string;
  city: string;
  postal_code: string;
}

interface ZemelapisCheckoutItem {
  tier: "single" | "gift_pair" | "gift_single";
  senderName?: string;
  recipientName?: string;
  personalMessage?: string;
}

interface CheckoutParams {
  books?: BookItem[];
  lessons?: LessonItem[];
  zemelapis?: ZemelapisCheckoutItem[];
  method: "stripe" | "paysera";
  currency?: string;
  discountCode?: string;
  shipping?: ShippingData;
  phone?: string;
  terminal?: TerminalData;
  shippingCost?: number;
  createAccount?: boolean;
  accountEmail?: string;
  accountPassword?: string;
}

export const useBookCheckout = () => {
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const initiateCheckout = async (params: CheckoutParams) => {
    setIsLoading(true);

    try {
      const body: Record<string, any> = {
        books: params.books || [],
        lessons: params.lessons || [],
        zemelapis: params.zemelapis || [],
        method: params.method,
        discountCode: params.discountCode || null,
        currency: params.currency || "EUR",
      };

      if (params.shipping) body.shipping = params.shipping;
      if (params.phone) body.phone = params.phone;
      if (params.terminal) body.terminal = params.terminal;
      if (params.shippingCost != null && params.shippingCost > 0) body.shippingCost = params.shippingCost;
      if (params.createAccount) {
        body.createAccount = true;
        body.accountEmail = params.accountEmail;
        body.accountPassword = params.accountPassword;
      }


      const { data, error } = await supabase.functions.invoke("create-book-checkout", { body });

      if (error) throw new Error(error.message);

      // Duplicate-purchase safeguard: backend refused to create a second identical order
      if (data?.duplicate) {
        toast({
          title: "No need to buy again",
          description: data.message || "This purchase is already in your account.",
        });
        setIsLoading(false);
        return;
      }

      if (data?.url) {
        window.location.href = data.url;
      } else {
        throw new Error("No checkout URL received");
      }

    } catch {
      toast({
        title: "Error",
        description: "Could not start the payment. Try again.",
        variant: "destructive",
      });
      setIsLoading(false);
    }
  };

  return { initiateCheckout, isLoading };
};
